"use client"

import { PadSessionStage, VPadStage } from "@/interfaces/virtualPad_types"
import { create } from "zustand"
import { devtools } from "zustand/middleware"

export interface PhysicalPadUI {
  index: number
  id: string
}

export interface VPadUI {
  connectionId: string
	stage: VPadStage
	deviceName?: string
  index: number
  connectedAt: number | null
}

interface PadsStore {
	stage: PadSessionStage
  vpSessionId: string | null

	physicalPads: Record<number, PhysicalPadUI>
	vpads: Record<string, VPadUI>

	setPhysicalPads: (gamepads: PhysicalPadUI[]) => void
  clearPhysicalPads: () => void

  setVpSessionId: (id: string | null) => void
	setStage: (stage: PadSessionStage) => void

	addVPad: (vPad: VPadUI) => void
	setVPadStage: (connectionId: string, stage: VPadStage) => void
	setVPadIndex: (connectionId: string, index: number) => void
	setVPadConnectedAt: (connectionId: string, connectedAt: number) => void
	removeVPad: (connectionId: string) => void
	clearVPads: () => void

	resetVPadSession: () => void
	reset: () => void
}

export const usePadsStore = create<PadsStore>()(
	devtools((set) => ({
		stage: PadSessionStage.NONE,
		vpSessionId: null,

		physicalPads: {},
		vpads: {},

		setPhysicalPads: (gamepads) => {
			set({
				physicalPads: Object.fromEntries(
					gamepads.map((gamepad) => [
						gamepad.index,
						gamepad
					])
				)
			})
		},

		clearPhysicalPads: () => {
			set({ physicalPads: {} })
		},

		setVpSessionId: (vpSessionId) => {
			set({ vpSessionId })
		},

		setStage: (stage) => {
			set({ stage })
		},

		addVPad: (vPad) => {
			set((state) => ({
				vpads: {
					...state.vpads,
					[vPad.connectionId]: vPad
				}
			}))
		},

		setVPadStage: (connectionId, stage) => {
			set((state) => {
				const vPad = state.vpads[connectionId]

				if (vPad === undefined) return state

				return {
					vpads: {
						...state.vpads,
						[connectionId]: {
							...vPad,
							stage
						}
					}
				}
			})
		},

		setVPadIndex: (connectionId, index) => {
			set((state) => {
				const vPad = state.vpads[connectionId]

				if (vPad === undefined) return state

				return {
					vpads: {
						...state.vpads,
						[connectionId]: {
							...vPad,
							index
						}
					}
				}
			})
		},

		setVPadConnectedAt: (connectionId, connectedAt) => {
			set((state) => {
				const vPad = state.vpads[connectionId]

				if (vPad === undefined) return state

				return {
					vpads: {
						...state.vpads,
						[connectionId]: {
							...vPad,
							connectedAt
						}
					}
				}
			})
		},

		removeVPad: (connectionId) => {
			set((state) => {
				const {
					[connectionId]: removedVPad,
					...remainingVPads
				} = state.vpads

				return {
					vpads: remainingVPads
				}
			})
		},

		clearVPads: () => {
			set({ vpads: {} })
		},

		resetVPadSession: () => {
			set({
				stage: PadSessionStage.NONE,
				vpSessionId: null
			})
		},

		reset: () => {
			set({
				stage: PadSessionStage.NONE,
				vpSessionId: null,
				physicalPads: {},
				vpads: {}
			})
		},

	}), {
		name: 'pads-store',
    enabled: process.env.NODE_ENV === 'development'
	})
)
